import { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import bgEdu from '@/assets/images/industries/edu.webp';
import bgProp from '@/assets/images/industries/property.webp';
import bgTech from '@/assets/images/industries/tech.webp';
import bgTourism from '@/assets/images/industries/tourism.webp';
import bgSports from '@/assets/images/industries/sport.webp';
import bgEngineering from '@/assets/images/industries/engineering.webp';



interface IndustryItem {
  id: string;
  name: string;
  Icon: string;
  bg: string;
  iconVariant?: any;
  items: string[];
  page: "home" | "contact" | "education" | "property" | "technology" | "tourism" | "sports" | "engineering";
}

const industries: IndustryItem[] = [
  {
    id: "01", name: "EDUCATION", Icon: bgEdu, bg: bgEdu,
    iconVariant: { hover: { y: [0, -8, 2, -3, 0], rotate: [0, -6, 6, -4, 0] } },
    items: ["School & university portals", "LMS & e-learning platforms", "Student management systems"],
    page: "education",
  },
  {
    id: "02", name: "PROPERTY", Icon: bgProp, bg: bgProp,
    iconVariant: { hover: { y: [0, -6, 0], scale: 1.15 } },
    items: ["Property listing portals", "Agent & tenant management", "Virtual tour integration"],
    page: "property",
  },
  {
    id: "03", name: "TECHNOLOGY", Icon: bgTech, bg: bgTech,
    iconVariant: { hover: { rotate: 90, scale: 1.18 } },
    items: ["SaaS product development", "API & system integration", "Cloud infrastructure"],
    page: "technology",
  },
  {
    id: "04", name: "TOURISM", Icon: bgTourism, bg: bgTourism,
    iconVariant: { hover: { x: [0, 8, -4, 6, 0], y: [0, -6, -2, -4, 0] } },
    items: ["Booking & reservation systems", "Tour package management", "Travel experience platforms"],
    page: "tourism",
  },
  {
    id: "05", name: "SPORT", Icon: bgSports, bg: bgSports,
    iconVariant: { hover: { rotate: [0, -8, 8, -4, 0], scale: 1.2 } },
    items: ["Club & league management", "Athlete performance tracking", "Event & ticketing systems"],
    page: "sports",
  },
  {
    id: "06", name: "ENGINEERING", Icon: bgEngineering, bg: bgEngineering,
    iconVariant: { hover: { rotate: [0, -12, 8, -8, 4, 0], scale: 1.18 } },
    items: ["Project & site management", "Equipment tracking systems", "Compliance & reporting tools"],
    page: "engineering",
  },
];

interface IndustriesProps {
  onNavigate?: (page: "home" | "contact" | "education" | "property" | "technology" | "tourism" | "sports" | "engineering", targetId?: string) => void;
}

export default function Industries({ onNavigate }: IndustriesProps) {
  return (
    <motion.section 
      id="industries" 
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-100px" }}
      transition={{ duration: 0.7, ease: "easeOut" }}
      className="relative bg-transparent py-16 md:py-24 w-full flex flex-col items-center justify-center"
    >
      {/* Section Header */}
      <div className="text-center relative z-20 mb-16 px-4 flex flex-col items-center">
        <h2 className="font-orbitron font-black text-3xl md:text-5xl lg:text-6xl text-white tracking-widest leading-none">
          Industries <span className="text-brand-purple font-semibold text-[0.8em] tracking-wider ml-1">Focus</span>
        </h2>
        <div className="mt-4 h-1.5 w-24 bg-linear-to-r from-brand-purple to-brand-electric rounded-full shadow-[0_0_10px_rgba(139,92,246,0.5)]" />
      </div>

      {/* Grid container */}
      <div className="relative z-10 w-full max-w-7xl px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {industries.map((item, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.5, delay: idx * 0.1 }}
              whileHover={{ y: -8 }}
              onClick={() => onNavigate?.(item.page)}
              className="relative overflow-hidden rounded-3xl border border-white/10 bg-slate-950/85 p-8 flex flex-col justify-between group cursor-pointer min-h-[380px] transition-all duration-300 hover:border-brand-purple/50 hover:shadow-[0_0_40px_rgba(139,92,246,0.35)]"
            >
              {/* Card Background Image with overlay */}
              <div className="absolute inset-0 z-0">
                <img
                  src={item.bg}
                  alt={item.name}
                  loading="lazy"
                  className="w-full h-full object-cover select-none opacity-30 group-hover:scale-110 transition-transform duration-700 ease-out"
                />
                {/* Cybernetic overlay mesh inside the card */}
                <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/85 to-slate-950/40" />
                <div className="absolute inset-0 bg-brand-purple/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              </div>

              {/* Card Header Content */}
              <div className="relative z-10">
                <div className="flex justify-between items-center mb-6">
                  <span className="font-mono text-xs tracking-[0.2em] text-brand-electric font-semibold uppercase">
                    COVERAGE // {item.id}
                  </span>
                  <div className="w-8 h-8 rounded-full bg-white/5 border border-white/10 flex items-center justify-center group-hover:border-brand-purple/50 group-hover:bg-brand-purple/10 transition-all">
                    <span className="w-2 h-2 rounded-full bg-brand-purple shadow-[0_0_8px_rgba(139,92,246,1)] group-hover:scale-125 transition-transform" />
                  </div>
                </div>

                <h3 className="font-orbitron font-black text-2xl md:text-3xl text-white tracking-tight uppercase group-hover:text-brand-electric transition-colors">
                  {item.name}
                </h3>

                <ul className="space-y-3 mt-6">
                  {item.items.map((bullet, j) => (
                    <li 
                      key={j}
                      className="font-mono text-sm text-slate-300 leading-snug flex items-center gap-2.5"
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-brand-purple shrink-0 group-hover:bg-brand-electric transition-colors" />
                      {bullet}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Card Bottom / Action */}
              <div className="relative z-10 mt-8 pt-4 border-t border-white/5 flex items-center justify-between">
                <span className="font-mono text-xs tracking-widest text-slate-400 group-hover:text-white transition-colors uppercase">
                  Explore System
                </span>
                <span className="font-mono text-sm text-white group-hover:text-brand-electric transition-colors translate-x-0 group-hover:translate-x-1.5 transition-transform duration-300">
                  →
                </span>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </motion.section>
  );
}
